import { useEffect, useState } from 'react';
import { WatchlistApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { handleAdminError, formatMonth } from '../../common/SharedFunctions';
import DataGrid, { Column, Paging, Pager } from 'devextreme-react/data-grid';
import { formatCurrency } from '@apply/ui';
import LoadPanel from 'devextreme-react/load-panel';
import { useNavigate } from 'react-router-dom';
import { WatchlistSummaryType } from '@apply/constants';

const SummaryTable = ({ summaryTypeId }) => {
    const [data, set_data] = useState([]);
    const [loadingMessage, set_loadingMessage] = useState('');
    const watchlistApi = new WatchlistApi.WatchlistApi(applicationInsights);
    const navigate = useNavigate();

    const formatDate = (dt) => {
        if (!dt) {
            return null;
        }
        return formatMonth(dt);
    };

    const onDateRender = (e) => {
        return formatDate(e.value);
    };

    const loadSummaryAsync = async (summaryTypeId) => {
        try {
            set_loadingMessage('Retrieving summary...');
            const response = await watchlistApi.getSummaryBySummaryType(summaryTypeId);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve data!');
            }
            const records = response.result;
            set_data(records);
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve records! Please refresh the page and try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        if (isNaN(summaryTypeId)) {
            return;
        }

        loadSummaryAsync(summaryTypeId);
    }, [summaryTypeId]);

    const onCurrencyRender = (e) => {
        return formatCurrency(e.value, 0);
    };

    const defaultPageSize = 10;

    const onRowClick = (e) => {
        switch (summaryTypeId) {
            case WatchlistSummaryType.Vertical:
                navigate(`/watchlists/vertical?wlid=${e.data.watchlistId}`);
                break;
            case WatchlistSummaryType.Rentals:
                navigate(`/watchlists/rentals?wlid=${e.data.watchlistId}`);
                break;
            case WatchlistSummaryType.SBA:
                navigate(`/watchlists/sba?wlid=${e.data.watchlistId}`);
                break;
        }
    };

    if (!data) {
        return null;
    }

    return (
        <>
            <DataGrid
                keyExpr={'id'}
                dataSource={data}
                rowAlternationEnabled={true}
                showBorders={true}
                showColumnHeaders={true}
                showRowLines={true}
                wordWrapEnabled={true}
                noDataText={'No data found for loan types'}
                remoteOperations={false}
                height={350}
                onRowClick={onRowClick}
            >
                <Column
                    dataField='startDate'
                    caption='Month'
                    width={80}
                    cellRender={onDateRender}
                />
                <Column
                    dataField='commitment'
                    caption='Total Committed'
                    width={120}
                    cellRender={onCurrencyRender}
                    alignment='left'
                />
                <Column
                    dataField='fundedAmount'
                    caption='Total Funded'
                    width={120}
                    cellRender={onCurrencyRender}
                    alignment='left'
                />
                <Paging
                    defaultPageSize={defaultPageSize}
                    enabled={true}
                    visible={true}
                />
                <Pager
                    showPageSizeSelector={false}
                    allowedPageSizes={[10]}
                    showNavigationButtons={true}
                    showInfo={true}
                    visible={true}
                />
            </DataGrid>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default SummaryTable;
